<template>
  <div class="growth-page">
    <!-- Initial load -->
    <div v-if="loading && !snapshot" class="growth-loading" role="status" aria-live="polite">
      <div class="growth-spinner"></div>
      <p>Loading your growth dashboard…</p>
    </div>

    <!-- Hard error (e.g. 404 / structured failure) — US-85 AC011 -->
    <div v-else-if="error && !snapshot" class="growth-error-banner" role="alert">
      <p>{{ error }}</p>
      <button type="button" class="rec-action" data-testid="reload-dashboard" @click="load">Retry</button>
    </div>

    <div v-else-if="snapshot" class="growth-layout">
      <!-- ── Header ── -->
      <header class="growth-header">
        <div>
          <h1 class="growth-title">Cross-Channel Performance</h1>
          <p class="growth-sub">
            One snapshot of traffic, search visibility, social distribution, and lead outcomes.
            <span class="growth-generated" data-testid="generated-at">Updated {{ formatTime(snapshot.generated_at) }}</span>
          </p>
        </div>
        <div class="growth-controls">
          <!-- Window (US-81 AC007 / US-82 AC004) -->
          <div class="seg" role="group" aria-label="Reporting window">
            <button
              v-for="w in windows"
              :key="w"
              type="button"
              class="seg-btn"
              :class="{ 'seg-btn-active': window === w }"
              :aria-pressed="window === w"
              :data-testid="`window-${w}`"
              @click="setWindow(w)"
            >{{ w }}</button>
          </div>
          <!-- Manual refresh (US-84 AC010) — disabled while in flight -->
          <button
            type="button"
            class="refresh-btn"
            data-testid="refresh"
            :disabled="loading"
            :aria-busy="loading"
            @click="load"
          >{{ loading ? 'Refreshing…' : '↻ Refresh' }}</button>
        </div>
      </header>

      <!-- Restricted-role banner (US-85 AC001) -->
      <div v-if="snapshot.role === 'restricted'" class="role-note" data-testid="role-restricted">
        You have a summary view. Trust diagnostics and advanced tools require analytics permission.
      </div>

      <!-- ── Channels (US-81 AC001) ── -->
      <section aria-label="Channel performance">
        <div class="chan-grid">
          <article
            v-for="ch in channelCards"
            :key="ch.key"
            class="chan-card"
            :data-testid="`channel-${ch.key}`"
          >
            <div class="chan-head">
              <h2 class="chan-title">{{ ch.label }}</h2>
              <span class="chan-badge" :class="`badge-${ch.status}`">{{ statusLabel(ch.status) }}</span>
            </div>

            <!-- Error state (US-81 AC005) — scoped, with retry -->
            <div v-if="ch.status === 'error'" class="chan-state chan-error">
              <p>{{ ch.error && ch.error.message ? ch.error.message : 'This channel failed to load.' }}</p>
              <button type="button" class="chan-retry" :data-testid="`retry-${ch.key}`" @click="load">Retry</button>
            </div>

            <!-- Empty / unavailable state (US-81 AC006) — no fabricated numbers -->
            <div v-else-if="ch.status !== 'ok'" class="chan-state chan-empty">
              <p>{{ ch.next_step || 'No data available yet.' }}</p>
            </div>

            <!-- Social referral: total + per-network share (US-81 AC009) -->
            <div v-else-if="ch.key === 'social_referral'" class="chan-body">
              <p class="chan-total">{{ ch.data.total.toLocaleString() }} <span>sessions</span></p>
              <ul class="referral-list">
                <li v-for="row in ch.data.sources" :key="row.source" class="referral-row">
                  <span>{{ row.source }}</span>
                  <span>{{ row.sessions.toLocaleString() }} · {{ row.share_percent }}%</span>
                </li>
              </ul>
            </div>

            <!-- Metric KPIs (traffic / seo / leads) — US-81 AC008, US-82 AC002 -->
            <div v-else class="chan-body">
              <div class="metric-grid">
                <div
                  v-for="m in ch.metrics"
                  :key="m.key"
                  class="metric"
                  :data-testid="`metric-${m.key}`"
                >
                  <div class="metric-name">
                    {{ m.displayName }}
                    <button
                      type="button"
                      class="metric-info"
                      :aria-label="`What is ${m.displayName}?`"
                      :aria-expanded="openDef === m.key"
                      @click="toggleDef(m.key)"
                    >ⓘ</button>
                  </div>
                  <div class="metric-value" :class="{ 'metric-empty': m.value === null }">
                    {{ m.value === null ? 'Unavailable' : m.formatted }}
                  </div>
                  <!-- Definition + attribution (US-82 AC002) -->
                  <div v-if="openDef === m.key && m.entry" class="metric-def">
                    <p>{{ m.entry.definition }}</p>
                    <p class="metric-def-meta"><strong>Formula:</strong> {{ m.entry.formula }}</p>
                    <p class="metric-def-meta"><strong>Attribution:</strong> {{ m.entry.attributionModel }} · {{ window }}</p>
                  </div>
                  <!-- US-82 AC008 unknown key fails safe -->
                  <div v-else-if="openDef === m.key && !m.entry" class="metric-def metric-def-missing">
                    Definition unavailable for this metric.
                  </div>
                </div>
              </div>
              <p v-if="ch.data.last_updated" class="chan-updated">
                Last updated {{ formatTime(ch.data.last_updated) }}
              </p>
            </div>
          </article>
        </div>
      </section>

      <div class="growth-cols">
        <div class="growth-col-main">
          <!-- ── Recommendations (US-83) ── -->
          <section class="panel" aria-label="Recommended next steps" data-testid="recommendations">
            <h2 class="panel-title">Recommended next steps</h2>
            <!-- Healthy state (US-83 AC005) -->
            <p v-if="!snapshot.recommendations.length" class="panel-healthy" data-testid="recs-healthy">
              ✓ Everything looks healthy — no priority actions right now.
            </p>
            <ol v-else class="rec-list">
              <li v-for="rec in snapshot.recommendations" :key="rec.id" class="rec-item" :data-testid="`rec-${rec.id}`">
                <span class="rec-priority" :aria-label="`Priority ${rec.priority}`">P{{ rec.priority }}</span>
                <div class="rec-body">
                  <h3 class="rec-title">{{ rec.title }}</h3>
                  <p class="rec-rationale">{{ rec.rationale }}</p>
                  <p class="rec-outcome">→ {{ rec.expected_outcome }}</p>
                </div>
                <button type="button" class="rec-action" @click="runAction(rec)">Start</button>
              </li>
            </ol>
          </section>

          <!-- ── Lead outcomes / correlation (US-85) ── -->
          <section class="panel" aria-label="Lead outcomes and drivers" data-testid="lead-outcomes">
            <h2 class="panel-title">Lead outcomes &amp; what drives them</h2>
            <p class="panel-note">{{ snapshot.correlation.note }}</p>

            <div v-if="clicksSeries.length >= 2" class="spark-wrap">
              <svg class="spark" :viewBox="`0 0 ${sparkW} ${sparkH}`" preserveAspectRatio="none" role="img"
                   aria-label="Organic clicks trend over time">
                <polyline :points="sparkPoints" fill="none" stroke="currentColor" stroke-width="2" />
              </svg>
              <span class="spark-cap">Organic clicks trend</span>
            </div>

            <!-- Impact ranking (US-85 AC006) -->
            <ul class="rank-list" data-testid="impact-ranking">
              <li v-for="row in snapshot.correlation.ranking" :key="row.channel" class="rank-row">
                <span class="rank-chan">{{ channelLabel(row.channel) }}</span>
                <span class="rank-dir" :class="`dir-${row.direction}`">{{ row.direction }}</span>
                <span class="rank-conf">{{ row.confidence }}</span>
                <span class="rank-coef">{{ row.coefficient === null ? '—' : row.coefficient }}</span>
              </li>
            </ul>

            <!-- Channel change events + before/after (US-85 AC007/AC008) -->
            <div class="events-wrap" data-testid="change-events">
              <h3 class="events-title">Channel changes</h3>
              <p v-if="!changeEvents.length" class="panel-note" data-testid="no-change-events">
                No channel changes detected in this period.
              </p>
              <ul v-else class="event-list">
                <li v-for="(ev, i) in changeEvents" :key="i" class="event-row" :data-testid="`event-${ev.channel}-${i}`">
                  <span class="event-marker" :class="`ev-${ev.type}`" aria-hidden="true">
                    {{ ev.type === 'increase' ? '▲' : '▼' }}
                  </span>
                  <div class="event-body">
                    <span class="event-label">
                      {{ channelLabel(ev.channel) }} {{ ev.type }} · {{ formatDate(ev.timestamp) }}
                    </span>
                    <span class="event-delta">
                      {{ ev.from }} → {{ ev.to }}
                      <template v-if="ev.before_after && ev.before_after.percent_delta !== null">
                        ({{ ev.before_after.percent_delta > 0 ? '+' : '' }}{{ ev.before_after.percent_delta }}% after)
                      </template>
                      <em v-if="ev.before_after && ev.before_after.partial_data" class="event-partial">partial data</em>
                    </span>
                  </div>
                </li>
              </ul>
            </div>
          </section>
        </div>

        <div class="growth-col-side">
          <!-- ── Trust panel (US-84) — owner only ── -->
          <section class="panel" aria-label="Data trust" data-testid="trust-panel">
            <h2 class="panel-title">Data trust</h2>
            <p v-if="!trust || trust.permitted === false" class="panel-note" data-testid="trust-restricted">
              Trust diagnostics require analytics permission.
            </p>
            <template v-else>
              <!-- Stale warning (US-84 AC008) -->
              <div v-if="trust.stale_warning" class="trust-stale" data-testid="stale-warning">
                ⚠ {{ trust.stale_warning.message }}
                <span v-if="trust.stale_warning.oldest_updated">
                  (oldest: {{ formatTime(trust.stale_warning.oldest_updated) }})
                </span>
              </div>
              <!-- Per-source indicators (US-84 AC001) -->
              <ul class="trust-src-list">
                <li v-for="src in trust.sources" :key="src.source" class="trust-src" :data-testid="`trust-${src.source}`">
                  <span class="trust-src-name">{{ channelLabel(src.source) }}</span>
                  <span class="trust-chip" :class="`fresh-${src.freshness.status}`">{{ src.freshness.status }}</span>
                  <span class="trust-chip" :class="`comp-${src.completeness.status}`">{{ src.completeness.completenessPercent }}%</span>
                  <span class="trust-chip" :class="`health-${src.connector_health.status}`">{{ src.connector_health.status }}</span>
                </li>
              </ul>
              <!-- Discrepancies (US-84 AC005/AC006/AC007) -->
              <p v-if="!trust.has_discrepancies" class="trust-ok" data-testid="no-discrepancies">
                ✓ {{ trust.no_discrepancy_message }}
              </p>
              <ul v-else class="disc-list">
                <li v-for="(d, i) in trust.discrepancies" :key="i" class="disc-item" :class="`sev-${d.severity}`">
                  <strong>{{ d.kpi_name }}</strong> — {{ d.variance_percent }}% variance
                  ({{ d.sources.join(' vs ') }})
                  <button type="button" class="disc-action">{{ d.action_label }}</button>
                </li>
              </ul>
              <p class="trust-checked">Checked {{ formatTime(trust.checked_at) }}</p>
            </template>
          </section>

          <!-- ── KPI dictionary (US-82 AC009) ── -->
          <section class="panel" aria-label="Metric definitions" data-testid="kpi-dictionary">
            <button type="button" class="dict-toggle" :aria-expanded="showDict" @click="showDict = !showDict">
              Metric definitions {{ showDict ? '▲' : '▼' }}
            </button>
            <p class="dict-version" data-testid="dict-version">
              v{{ snapshot.dictionary.version }} · updated {{ formatDate(snapshot.dictionary.updated_at) }}
            </p>
            <dl v-if="showDict" class="dict-list">
              <template v-for="e in snapshot.dictionary.entries" :key="e.metricKey">
                <dt class="dict-term">{{ e.displayName }}</dt>
                <dd class="dict-def">{{ e.definition }}</dd>
              </template>
            </dl>
          </section>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, computed, onMounted, onUnmounted } from 'vue'
import { useRouter } from 'vue-router'
import api from '../utils/api'

const WINDOWS = ['24h', '7d', '30d', '90d']
const REFRESH_MS = 30000 // US-81 AC003 near-live refresh

const CHANNEL_ORDER = [
  { key: 'traffic', label: 'Traffic' },
  { key: 'seo', label: 'SEO / AEO / GEO' },
  { key: 'social_referral', label: 'Social Referral' },
  { key: 'lead_outcomes', label: 'Lead Outcomes' },
]
const CHANNEL_LABELS = {
  traffic: 'Traffic', seo: 'SEO', social_referral: 'Social', lead_outcomes: 'Leads', social: 'Social',
}
// Metric key → channel data ordering for card rendering.
const CHANNEL_METRIC_KEYS = {
  traffic: ['organic_clicks', 'organic_impressions', 'organic_ctr', 'average_position', 'page_speed'],
  seo: ['technical_seo', 'aeo_score', 'geo_score'],
  lead_outcomes: ['total_leads', 'qualified_leads', 'won_leads', 'conversion_rate'],
}

export default {
  name: 'GrowthDashboard',
  props: { id: { type: String, required: true } },
  setup(props) {
    const router = useRouter()
    const loading = ref(false)
    const error = ref('')
    const snapshot = ref(null)
    const window = ref('30d')
    const openDef = ref(null)
    const showDict = ref(false)

    // US-81 AC003 / US-85 AC011 — latest-request-wins guard. Only the response
    // for the most recent request may update the view.
    let requestSeq = 0
    let timer = null

    const load = async () => {
      const seq = ++requestSeq
      loading.value = true
      error.value = ''
      try {
        const res = await api.get(`/sites/${props.id}/growth-dashboard`, { params: { window: window.value } })
        if (seq !== requestSeq) return // a newer request superseded this one
        snapshot.value = res.data
      } catch (e) {
        if (seq !== requestSeq) return
        error.value = e.response?.status === 404
          ? 'This site could not be found.'
          : (e.response?.data?.error || 'Failed to load the growth dashboard.')
      } finally {
        if (seq === requestSeq) loading.value = false
      }
    }

    const setWindow = (w) => { if (w !== window.value) { window.value = w; load() } }

    // Build the dictionary lookup for definitions/formatting.
    const dictByKey = computed(() => {
      const map = {}
      const entries = snapshot.value?.dictionary?.entries || []
      for (const e of entries) map[e.metricKey] = e
      return map
    })

    const formatValue = (value, entry) => {
      if (value === null || value === undefined) return 'Unavailable'
      const fmt = entry ? entry.unitOrFormat : 'integer'
      switch (fmt) {
        case 'percent': return `${(value <= 1 ? value * 100 : value).toFixed(1)}%`
        case 'score_100': return `${Math.round(value)}/100`
        case 'position': return Number(value).toFixed(1)
        default: return Number(value).toLocaleString()
      }
    }

    // Assemble per-channel card view models.
    const channelCards = computed(() => {
      if (!snapshot.value) return []
      return CHANNEL_ORDER.map((meta) => {
        const block = snapshot.value.channels[meta.key] || { status: 'unavailable', data: null, error: null }
        const card = {
          key: meta.key,
          label: meta.label,
          status: block.status,
          data: block.data,
          error: block.error,
          next_step: block.next_step,
          metrics: [],
        }
        if (block.status === 'ok' && block.data && CHANNEL_METRIC_KEYS[meta.key]) {
          card.metrics = CHANNEL_METRIC_KEYS[meta.key].map((k) => {
            const entry = dictByKey.value[k] || null
            const value = block.data[k] === undefined ? null : block.data[k]
            return { key: k, displayName: entry ? entry.displayName : k, value, formatted: formatValue(value, entry), entry }
          })
        }
        return card
      })
    })

    const trust = computed(() => snapshot.value?.trust || null)

    const clicksSeries = computed(() => {
      const buckets = snapshot.value?.correlation?.buckets || []
      return buckets.map((b) => b.traffic).filter((v) => typeof v === 'number')
    })
    const changeEvents = computed(() => snapshot.value?.correlation?.change_events || [])

    const sparkW = 240
    const sparkH = 48
    const sparkPoints = computed(() => {
      const vals = clicksSeries.value
      if (vals.length < 2) return ''
      const max = Math.max(...vals); const min = Math.min(...vals); const span = max - min || 1
      return vals.map((v, i) => {
        const x = (i / (vals.length - 1)) * sparkW
        const y = sparkH - ((v - min) / span) * (sparkH - 4) - 2
        return `${x.toFixed(1)},${y.toFixed(1)}`
      }).join(' ')
    })

    const toggleDef = (key) => { openDef.value = openDef.value === key ? null : key }
    const channelLabel = (c) => CHANNEL_LABELS[c] || c
    const statusLabel = (s) => ({ ok: 'Live', empty: 'No data', unavailable: 'Not connected', error: 'Error' }[s] || s)

    const formatTime = (iso) => {
      if (!iso) return ''
      const diff = Date.now() - new Date(iso).getTime()
      if (Number.isNaN(diff)) return ''
      const m = Math.round(diff / 60000)
      if (m < 1) return 'just now'
      if (m < 60) return `${m}m ago`
      const h = Math.round(m / 60)
      if (h < 24) return `${h}h ago`
      return `${Math.round(h / 24)}d ago`
    }
    const formatDate = (iso) => (iso ? new Date(iso).toISOString().slice(0, 10) : '')

    // US-83 AC003/AC004 — action routing keeps the user in-product and carries
    // recommendation context.
    const runAction = (rec) => {
      const a = rec.action || {}
      if (a.route === 'SiteSettings') router.push({ name: 'SiteSettings', params: { id: props.id }, query: { rec: a.recommendation_id, intent: a.intent } })
      else if (a.route === 'social') router.push({ name: 'social', params: { id: props.id }, query: { rec: a.recommendation_id } })
      else if (a.route === 'editor') router.push({ name: 'editor', params: { id: props.id } })
      else if (a.endpoint) {
        api({ method: a.method || 'POST', url: a.endpoint.replace(':id', props.id) }).then(load).catch(() => {})
      } else router.push({ name: 'sites' })
    }

    // US-81 AC004 — immediate post-publish visibility. Publishing happens in
    // the editor; when the user returns to this tab/route the dashboard
    // refetches so the snapshot reflects the just-published state well within
    // the 60s target, without waiting for the 30s poll.
    const onVisible = () => {
      if (typeof document !== 'undefined' && document.visibilityState === 'visible' && !loading.value) load()
    }

    onMounted(() => {
      load()
      timer = setInterval(() => { if (!loading.value) load() }, REFRESH_MS)
      // NOTE: the `window` ref shadows the global; use globalThis for the DOM.
      if (typeof document !== 'undefined') document.addEventListener('visibilitychange', onVisible)
      if (typeof globalThis !== 'undefined' && globalThis.addEventListener) globalThis.addEventListener('focus', onVisible)
    })
    onUnmounted(() => {
      if (timer) clearInterval(timer)
      if (typeof document !== 'undefined') document.removeEventListener('visibilitychange', onVisible)
      if (typeof globalThis !== 'undefined' && globalThis.removeEventListener) globalThis.removeEventListener('focus', onVisible)
    })

    return {
      loading, error, snapshot, window, windows: WINDOWS, openDef, showDict,
      channelCards, trust, clicksSeries, changeEvents, sparkW, sparkH, sparkPoints,
      load, setWindow, toggleDef, channelLabel, statusLabel, formatTime, formatDate, runAction,
    }
  },
}
</script>

<style scoped>
.growth-page { padding: 24px; max-width: 1200px; margin: 0 auto; }
.growth-loading { display: flex; flex-direction: column; align-items: center; gap: 12px; padding: 64px 0; color: var(--text-secondary); }
.growth-spinner { width: 32px; height: 32px; border: 3px solid var(--glass-border); border-top-color: var(--accent); border-radius: 50%; animation: spin 0.8s linear infinite; }
@keyframes spin { to { transform: rotate(360deg); } }
.growth-error-banner { background: rgba(248,113,113,0.10); color: var(--danger); border: 1px solid rgba(248,113,113,0.25); border-radius: 10px; padding: 16px; }

.growth-header { display: flex; justify-content: space-between; align-items: flex-start; gap: 16px; flex-wrap: wrap; margin-bottom: 16px; }
.growth-title { font-size: 26px; font-weight: 700; color: var(--text-primary); margin: 4px 0; letter-spacing: -0.01em; }
.growth-sub { color: var(--text-secondary); margin: 0; font-size: 14px; }
.growth-generated { color: var(--text-muted); margin-left: 6px; }
.growth-controls { display: flex; flex-direction: column; gap: 8px; align-items: flex-end; }
.seg { display: inline-flex; background: var(--glass-bg); border: var(--glass-border); border-radius: 8px; padding: 3px; gap: 2px; }
.seg-btn { border: none; background: transparent; padding: 6px 12px; border-radius: 6px; font-size: 13px; font-weight: 500; color: var(--text-secondary); cursor: pointer; transition: color 0.15s, background 0.15s; }
.seg-btn:hover { color: var(--text-primary); }
.seg-btn-active { background: var(--glass-bg-active); color: var(--accent-light); }
.refresh-btn { border: var(--glass-border); background: var(--glass-bg); border-radius: 8px; padding: 6px 12px; font-size: 13px; color: var(--text-secondary); cursor: pointer; transition: background 0.15s, color 0.15s; }
.refresh-btn:hover:not(:disabled) { background: var(--glass-bg-hover); color: var(--text-primary); }
.refresh-btn:disabled { opacity: 0.6; cursor: default; }

.role-note { background: var(--accent-soft); border: 1px solid rgba(124,109,250,0.25); color: var(--text-secondary); border-radius: 10px; padding: 10px 14px; font-size: 13px; margin-bottom: 16px; }

.chan-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(260px, 1fr)); gap: 16px; margin-bottom: 24px; }
.chan-card { background: var(--surface-1); border: var(--glass-border); border-radius: 12px; padding: 16px; }
.chan-head { display: flex; justify-content: space-between; align-items: center; margin-bottom: 12px; }
.chan-title { font-size: 15px; font-weight: 600; color: var(--text-primary); margin: 0; }
.chan-badge { font-size: 11px; text-transform: uppercase; letter-spacing: 0.04em; font-weight: 700; padding: 2px 7px; border-radius: 999px; }
.badge-ok { background: rgba(52,211,153,0.12); color: var(--success); } .badge-empty { background: var(--glass-bg); color: var(--text-muted); }
.badge-unavailable { background: rgba(251,191,36,0.12); color: var(--warning); } .badge-error { background: rgba(248,113,113,0.12); color: var(--danger); }
.chan-state { font-size: 13px; color: var(--text-secondary); }
.chan-error { color: var(--danger); }
.chan-retry, .disc-action { margin-top: 8px; border: var(--glass-border); background: var(--glass-bg); color: var(--text-secondary); border-radius: 6px; padding: 4px 10px; font-size: 12px; cursor: pointer; transition: background 0.15s, color 0.15s; }
.chan-retry:hover, .disc-action:hover { background: var(--glass-bg-hover); color: var(--text-primary); }
.chan-total { font-size: 24px; font-weight: 700; color: var(--text-primary); margin: 0 0 8px; font-variant-numeric: tabular-nums; }
.chan-total span { font-size: 13px; font-weight: 400; color: var(--text-muted); }
.referral-list, .trust-src-list, .rank-list, .rec-list, .disc-list, .dict-list { list-style: none; margin: 0; padding: 0; }
.referral-row { display: flex; justify-content: space-between; font-size: 13px; color: var(--text-secondary); padding: 3px 0; }
.metric-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 12px; }
.metric-name { font-size: 12px; color: var(--text-secondary); display: flex; align-items: center; gap: 4px; }
.metric-info { border: none; background: transparent; color: var(--text-muted); cursor: pointer; font-size: 12px; padding: 0; }
.metric-value { font-size: 20px; font-weight: 700; color: var(--text-primary); font-variant-numeric: tabular-nums; }
.metric-empty { color: var(--text-muted); font-size: 14px; font-weight: 500; }
.metric-def { grid-column: 1 / -1; font-size: 12px; color: var(--text-secondary); background: var(--glass-bg); border-radius: 8px; padding: 8px; }
.metric-def p { margin: 0 0 4px; }
.metric-def-meta { color: var(--text-muted); }
.metric-def-missing { color: var(--warning); }
.chan-updated { font-size: 11px; color: var(--text-muted); margin: 12px 0 0; }

.growth-cols { display: grid; grid-template-columns: 1.5fr 1fr; gap: 20px; }
@media (max-width: 900px) { .growth-cols { grid-template-columns: 1fr; } .growth-controls { align-items: flex-start; } .metric-grid { grid-template-columns: 1fr 1fr; } }
.growth-col-main, .growth-col-side { display: flex; flex-direction: column; gap: 20px; }
.panel { background: var(--surface-1); border: var(--glass-border); border-radius: 12px; padding: 18px; }
.panel-title { font-size: 16px; font-weight: 600; color: var(--text-primary); margin: 0 0 12px; }
.panel-note { color: var(--text-secondary); font-size: 13px; margin: 0 0 12px; }
.panel-healthy { color: var(--success); font-size: 14px; }

.rec-list { display: flex; flex-direction: column; gap: 12px; }
.rec-item { display: flex; align-items: flex-start; gap: 12px; padding: 12px; border: var(--glass-border); border-radius: 10px; }
.rec-priority { font-size: 11px; font-weight: 700; padding: 3px 7px; border-radius: 6px; background: var(--accent-soft); color: var(--accent-light); flex-shrink: 0; }
.rec-body { flex: 1; }
.rec-title { font-size: 14px; font-weight: 600; color: var(--text-primary); margin: 0 0 2px; }
.rec-rationale { font-size: 13px; color: var(--text-secondary); margin: 0 0 2px; }
.rec-outcome { font-size: 12px; color: var(--success); margin: 0; }
.rec-action { flex-shrink: 0; background: var(--accent); color: #fff; border: none; border-radius: 8px; padding: 8px 14px; font-size: 13px; font-weight: 500; cursor: pointer; box-shadow: 0 0 18px var(--accent-glow); transition: background 0.15s; }
.rec-action:hover { background: #6d5ef0; }

.spark-wrap { color: var(--accent); margin-bottom: 12px; }
.spark { width: 100%; height: 48px; }
.spark-cap { font-size: 12px; color: var(--text-muted); }
.rank-list { display: flex; flex-direction: column; gap: 8px; }
.rank-row { display: grid; grid-template-columns: 1fr auto auto auto; gap: 8px; font-size: 13px; color: var(--text-secondary); align-items: center; }
.rank-dir { font-size: 11px; text-transform: uppercase; letter-spacing: 0.04em; font-weight: 600; }
.dir-positive { color: var(--success); } .dir-negative { color: var(--danger); } .dir-inconclusive { color: var(--text-muted); }
.rank-conf { font-size: 11px; color: var(--text-muted); }
.rank-coef { font-variant-numeric: tabular-nums; color: var(--text-primary); }

.events-wrap { margin-top: 16px; border-top: var(--glass-border); padding-top: 12px; }
.events-title { font-size: 13px; font-weight: 600; color: var(--text-primary); margin: 0 0 8px; }
.event-list { list-style: none; margin: 0; padding: 0; display: flex; flex-direction: column; gap: 8px; }
.event-row { display: flex; gap: 8px; align-items: flex-start; font-size: 13px; }
.event-marker { font-size: 11px; line-height: 18px; }
.ev-increase { color: var(--success); } .ev-decrease { color: var(--danger); }
.event-body { display: flex; flex-direction: column; }
.event-label { color: var(--text-primary); font-weight: 500; }
.event-delta { color: var(--text-secondary); font-variant-numeric: tabular-nums; }
.event-partial { color: var(--warning); font-style: italic; margin-left: 4px; }

.trust-stale { background: rgba(251,191,36,0.10); border: 1px solid rgba(251,191,36,0.25); color: var(--warning); border-radius: 8px; padding: 8px 10px; font-size: 13px; margin-bottom: 12px; }
.trust-src { display: flex; align-items: center; gap: 6px; padding: 6px 0; font-size: 13px; }
.trust-src-name { flex: 1; color: var(--text-primary); font-weight: 500; }
.trust-chip { font-size: 11px; text-transform: uppercase; letter-spacing: 0.04em; font-weight: 600; padding: 2px 6px; border-radius: 999px; background: var(--glass-bg); color: var(--text-secondary); }
.fresh-fresh, .health-healthy, .comp-complete { background: rgba(52,211,153,0.12); color: var(--success); }
.fresh-stale, .health-error, .health-disconnected, .comp-insufficient { background: rgba(248,113,113,0.12); color: var(--danger); }
.fresh-aging, .health-degraded, .comp-partial { background: rgba(251,191,36,0.12); color: var(--warning); }
.trust-ok { color: var(--success); font-size: 13px; margin: 12px 0 0; }
.disc-list { display: flex; flex-direction: column; gap: 8px; margin-top: 12px; }
.disc-item { font-size: 13px; padding: 8px; border-radius: 8px; }
.sev-warning { background: rgba(251,191,36,0.10); border: 1px solid rgba(251,191,36,0.2); } .sev-critical { background: rgba(248,113,113,0.10); border: 1px solid rgba(248,113,113,0.2); }
.trust-checked { font-size: 11px; color: var(--text-muted); margin: 12px 0 0; }
.dict-toggle { border: none; background: transparent; font-size: 14px; font-weight: 600; color: var(--accent-light); cursor: pointer; padding: 0; }
.dict-version { font-size: 11px; color: var(--text-muted); margin: 4px 0 0; }
.dict-list { margin-top: 12px; }
.dict-term { font-size: 13px; font-weight: 600; color: var(--text-primary); margin-top: 8px; }
.dict-def { font-size: 12px; color: var(--text-secondary); margin: 2px 0 0; }
</style>
